import styled from "@emotion/styled"
import { Button, keyframes, Tabs } from "@mui/material"
import Image from "next/image"
import { useMemo, useRef, useState } from "react"
import { Transition, TransitionStatus } from "react-transition-group"
import MyTabs from "reusable/MyTabs"
import colors from "theme/colors"
import { Desktop, ImageContainer, Mobile, NewText } from "ui"
import { extractProperty } from "utils"
import { httpsImage } from "utils/utils"

const StyledContainer = styled(Tabs)`
  width: 100%;
  height: 90%;
  overflow: hidden;
  position: relative;
`
const IconsBox = styled.div`
  width: 100%;
  height: 250px;
  ${(props) => props.theme.breakpoints.down("md")} {
    max-width: 350px;
    justify-self: center;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    height: 100%;
  }
`

interface LogoProps {
  top: string
  left: string
  bottom: string
  right: string
  x: string
  y: string
  isSelected: boolean
  animDelay: string
}

const floatAnimation = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(10px); // Adjust the pixel value to control the floating range
  }
`

const getRandomDelay = () => `${Math.random() * 5}s`

const FloatingWrapper = styled.div<{ animDelay: string }>`
  width: 100%;
  height: 100%;
  animation-delay: ${(props) => props.animDelay} !important;
  animation: ${floatAnimation} 6s ease-in-out infinite;
`

const Logo = styled(Button, {
  shouldForwardProp: (prop) =>
    prop !== "isSelected" &&
    prop !== "top" &&
    prop !== "left" &&
    prop !== "bottom" &&
    prop !== "right" &&
    prop !== "x" &&
    prop !== "y" &&
    prop !== "animDelay",
}) <LogoProps>`
padding:0;
background-color:transparent !important;
  width: 70px;
  height: 70px;
  transform: ${(props) => props.isSelected && "scale(1.2)"};
  position: absolute;
  top: ${(props) => props.top};
  left: ${(props) => props.left};
  bottom: ${(props) => props.bottom};
  right: ${(props) => props.right};
  transition:
    transform 0.6s ease-in-out,
    transform 0.6s ease-in-out;
  cursor: pointer;
  &:focus-visible {
    transform: scale(1.2);
    outline: 2px solid blue;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    width: 80px;
    height: 80px;
    transform: ${(props) => props.isSelected && "scale(1.66)"};
    transition:
      transform 0.6s ease-in-out,
      transform 0.6s ease-in-out;
    &:focus-visible {
      transform: scale(1.66);
      outline: 2px solid blue;
    }
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    width: 130px;
    height: 130px;
    transform: ${(props) => props.isSelected && "scale(1.53)"};
    transition:
      transform 0.6s ease-in-out,
      transform 0.6s ease-in-out;
    &:focus-visible {
      transform: scale(1.53);
      outline: 2px solid blue;
    }
  }
`

const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 20px;

  ${(props) => props.theme.breakpoints.up("md")} {
    grid-gap: 75px;
    grid-template-columns: 1fr 1fr;
    height: 398px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    height: 598px;
    grid-gap: 110px;
  }
`

const StyledBackground = styled.div<{ defaultBackground: boolean }>`
     position: relative;
  ${(props) =>
    props.defaultBackground &&
    `
      background: linear-gradient(
        180deg,
        rgba(21, 62, 105, 0.06) 0%,
        rgba(51, 67, 84, 0) 100%
        );
    `
  }
`;
const StyledWallet = styled.div`
  height: 100%;
  aspect-ratio: 755/599;

  ${(props) => props.theme.breakpoints.up("md")} {
    /* margin: 68px 0; */
    height: calc(100% - 68px);
  }
  background-repeat: no-repeat;
  position: relative;
`

const StyledInfoContainer = styled.div`
  position: absolute;
  top: 50%; // Start at the vertical center of StyledWallet
  left: 50%; // Start at the horizontal center of StyledWallet
  transform: translate(
    -50%,
    0
  ); // Shift it to be centered and 20% below the center
  transition: opacity 0.5s ease-in-out;
  display: flex;
  width: 290px;
  gap: 8px;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 10px;
    min-width: 350px;
    width: 90%;
  }
`

const StyledText = styled.div`
  z-index: 5;
  p {
    margin: 0px;
  }
`

const Padding = styled.div<{ defaultPadding: boolean }>`
  ${(props) =>
    props.defaultPadding &&
    ` padding: 32px 20px;
      ${props.theme.breakpoints.up("md")} {
        padding: 45px 106px;
      }
      ${props.theme.breakpoints.up("lg")} {
        padding: 68px 160px;
      }`
  }
`;

const duration = 1000

const defaultStyle = {
  transition: `opacity ${duration}ms ease-in-out`,
  opacity: 0,
}

const transitionStyles: Record<TransitionStatus, { opacity: number }> = {
  entering: { opacity: 1 },
  entered: { opacity: 1 },
  exiting: { opacity: 0 },
  exited: { opacity: 0 },
  unmounted: { opacity: 0 },
}

function Fade({
  in: inProp,
  logo,
  title,
  text,
}: {
  in: boolean
  logo: any
  title: any
  text: any
}) {
  const nodeRef = useRef(null)
  return (
    <Transition nodeRef={nodeRef} in={inProp} timeout={duration}>
      {(state) => (
        <StyledInfoContainer
          id="selectedDetail"
          ref={nodeRef}
          style={{
            ...defaultStyle,
            ...transitionStyles[state],
          }}
          tabIndex={0}
        >
          <ImageContainer
            desktopUrl={logo}
            height={110}
            width={110}
            mediumHeight={100}
            mediumWidth={100}
            mobileHeight={60}
            mobileWidth={60}
            sm
            loading="eager"
            emptyAlt
          />

          <StyledText>
            <NewText
              component="h3"
              desktopFontWeight={500}
              largeDesktopFontSize={45}
              largeDesktopLineHeight="63.75px"
              desktopFontSize={30}
              desktopLineHeight={"42px"}
              mobileFontSize={28}
              mobileLineHeight={"36px"}
              color={colors.black}
              style={{ paddingTop: 5 }}
            >
              {title}
            </NewText>
            <NewText
              component="div"
              desktopFontWeight={400}
              largeDesktopFontSize={34}
              largeDesktopLineHeight="47px"
              desktopFontSize={22}
              desktopLineHeight={"32px"}
              mobileFontSize={16}
              mobileLineHeight={"24px"}
              color={colors.black}
              dangerouslySetInnerHTML={{ __html: text }}
            />
          </StyledText>
        </StyledInfoContainer>
      )}
    </Transition>
  )
}

type Props = {
  data: any,
  defaultPadding?: boolean
  defaultBackground?: boolean
}

const positions: {
  top: string
  left: string
  right: string
  bottom: string
}[] = [
    { top: "10%", left: "20%", right: "auto", bottom: "auto" },
    { top: "10%", left: "65%", right: "auto", bottom: "auto" },
    { top: "40%", left: "3%", right: "auto", bottom: "auto" },
    { top: "35%", left: "40%", right: "auto", bottom: "auto" },
    { top: "40%", left: "80%", right: "auto", bottom: "auto" },
    { top: "55%", left: "20%", right: "auto", bottom: "auto" },
    { top: "55%", left: "62%", right: "auto", bottom: "auto" },
    { top: "70%", left: "40%", right: "auto", bottom: "auto" },
    { top: "70%", left: "80%", right: "auto", bottom: "auto" },
    { top: "75%", left: "3%", right: "auto", bottom: "auto" },
  ]

function Wallet({ data, defaultPadding = true, defaultBackground = true }: Props) {
  const [selectedTab, setSelectedTab] = useState<number>(0)
  const tabs = useMemo(
    () => [
      {
        title: extractProperty(data, "tabTitle"),
        data: extractProperty(data, "walletElements"),
      },
      {
        title: extractProperty(data, "tabTitle2"),
        data: extractProperty(data, "walletContent2"),
      },
    ],
    [data],
  )
  const [selectedLogo, setSelectedLogo] = useState<number>(0)
  const ref = useRef<HTMLButtonElement | null>(null)
  const [fade, setFade] = useState(true)
  const handleLogoClick = (id: number): void => {

    setSelectedLogo(id)
    setFade(false)

    setTimeout(() => {
      setFade(true)
    }, 30) // 3000 milliseconds = 3 seconds
  }

  const log = extractProperty(tabs?.[selectedTab]?.data?.[selectedLogo], "logo")
  const walletTitle = extractProperty(
    tabs?.[selectedTab]?.data?.[selectedLogo],
    "walletTitle",
  )
  const walletText = extractProperty(
    tabs?.[selectedTab]?.data?.[selectedLogo],
    "walletText",
  )
  const arr = useMemo(
    () => [...(tabs?.[selectedTab]?.data || [])],
    [tabs, selectedTab],
  )
  const delays = useMemo(() => arr.map(() => getRandomDelay()), [arr])
  return (
    <StyledBackground defaultBackground={defaultBackground}>
      <Padding defaultPadding={defaultPadding} >
        <MyTabs
          tabs={tabs.map((item, index) => ({ id: index, label: item.title }))}
          setSelectedTab={setSelectedTab}
          selectedTab={selectedTab}
        />
        <StyledGrid>
          <IconsBox>
            <StyledContainer ref={ref}>
              <Image
                quality={85}
                src={"/images/confetti.svg"}
                alt=""
                fill
                role="presentation"
                loading="eager"
                style={{ zIndex: -1, objectFit: "cover" }}
              />
              {arr.map((logo: any, index: number) => {
                const url = extractProperty(logo, "logo")
                const logoAltText = extractProperty(logo, "logoAltText")
                // const delay = useMemo(getRandomDelay(), [])
                return (
                  <Logo
                    role="tab"
                    aria-selected={selectedLogo === index}
                    aria-controls={`selectedDetail`}
                    disableRipple={true}
                    animDelay={delays[index]}
                    x="1"
                    y="1"
                    bottom={positions[index].bottom}
                    right={positions[index].right}
                    key={index.toString()}
                    top={positions[index].top}
                    left={positions[index].left}
                    isSelected={selectedLogo === index}
                    onMouseEnter={() => handleLogoClick(index)}
                    tabIndex={selectedLogo === index ? 0 : -1}
                    id={`logo-${index}`}
                    onKeyDown={(e) => {
                      if (e.key === "Tab" && !e.shiftKey) {
                        e.preventDefault();
                        const selectedDetail = document?.getElementById(`selectedDetail`);
                        if (selectedDetail) {
                          selectedDetail.focus();
                        }
                      } else if (e.key === "ArrowLeft") {
                        e.preventDefault();
                        const nextIndex = (index + 1) % arr.length;
                        handleLogoClick(nextIndex);

                      } else if (e.key === "ArrowRight") {
                        e.preventDefault();
                        const prevIndex = index === 0 ? arr.length - 1 : index - 1;
                        handleLogoClick(prevIndex);

                      }
                    }}
                  >
                    <FloatingWrapper animDelay={`${index * 800}ms`}>
                      <Image
                        quality={85}
                        src={httpsImage(url || "")}
                        fill
                        alt={logoAltText}
                        loading="eager"
                      />
                    </FloatingWrapper>
                  </Logo>
                )
              })}
            </StyledContainer>
          </IconsBox>
          <StyledWallet>
            <Mobile>
              <Image priority fill alt="" src={"/wallet-mobile.png"} />
            </Mobile>
            <Desktop>
              <Image priority fill alt="" src={"/wallet-desktop.png"} />
            </Desktop>
            <Fade
              key={selectedLogo}
              in={fade}
              logo={log}
              title={walletTitle}
              text={walletText}
            />
          </StyledWallet>
        </StyledGrid>
      </Padding>
    </StyledBackground>
  )
}

export default Wallet
